import React, { createContext, useContext, useReducer, useEffect } from "react";
import { Project, ProjectsState } from "@/types/types";
import { projectService } from "@/services/projectService";
import { queryClient } from "@/lib/react-query";

// Define action types
type ProjectsAction =
  | { type: "FETCH_PROJECTS_START" }
  | { type: "FETCH_PROJECTS_SUCCESS"; payload: Project[] }
  | { type: "FETCH_PROJECTS_ERROR"; payload: Error }
  | { type: "SELECT_PROJECT"; payload: Project | null };

// Initial state
const initialState: ProjectsState = {
  projects: [],
  selectedProject: null,
  isLoading: false,
  error: null,
};

// Create reducer
const projectsReducer = (
  state: ProjectsState,
  action: ProjectsAction
): ProjectsState => {
  switch (action.type) {
    case "FETCH_PROJECTS_START":
      return {
        ...state,
        isLoading: true,
        error: null,
      };
    case "FETCH_PROJECTS_SUCCESS":
      return {
        ...state,
        projects: action.payload,
        isLoading: false,
        error: null,
      };
    case "FETCH_PROJECTS_ERROR":
      return {
        ...state,
        isLoading: false,
        error: action.payload,
      };
    case "SELECT_PROJECT":
      return {
        ...state,
        selectedProject: action.payload,
      };
    default:
      return state;
  }
};

// Create context
const ProjectsContext = createContext<{
  state: ProjectsState;
  dispatch: React.Dispatch<ProjectsAction>;
}>({
  state: initialState,
  dispatch: () => null,
});

// Create provider
export const ProjectsProvider: React.FC<{ children: React.ReactNode }> = ({
  children,
}) => {
  const [state, dispatch] = useReducer(projectsReducer, initialState);

  useEffect(() => {
    const fetchProjects = async () => {
      dispatch({ type: "FETCH_PROJECTS_START" });
      try {
        const projects = await projectService.getProjects();
        dispatch({ type: "FETCH_PROJECTS_SUCCESS", payload: projects });
      } catch (error) {
        dispatch({ type: "FETCH_PROJECTS_ERROR", payload: error as Error });
      }
    };

    fetchProjects();

    // Set up real-time subscription
    const subscription = projectService.subscribeToChanges(() => {
      // Invalidate and refetch when data changes
      queryClient.invalidateQueries({ queryKey: ["projects"] });
      fetchProjects();
    });

    return () => {
      subscription.unsubscribe();
    };
  }, []);

  return (
    <ProjectsContext.Provider value={{ state, dispatch }}>
      {children}
    </ProjectsContext.Provider>
  );
};

// Create hook for using the context
export const useProjects = () => useContext(ProjectsContext);
